import type { ColDef, ColGroupDef } from "ag-grid-community";

const NUM_FILTER = "agNumberColumnFilter";

function isColumnGroup(def: ColDef | ColGroupDef): def is ColGroupDef {
  return (def as ColGroupDef).children != null;
}

function typeIncludesNumberAlignment(colDef: ColDef): boolean {
  if (!colDef.type) return false;
  const types = Array.isArray(colDef.type) ? colDef.type : [colDef.type];
  return (
    (types as string[]).includes("rightAligned") ||
    (types as string[]).includes("numericColumn")
  );
}

function isNumericLayoutColumn(c: ColDef): boolean {
  if (c.filter === NUM_FILTER) return true;
  if (c.cellDataType === "number") return true;
  if (typeIncludesNumberAlignment(c)) return true;
  return false;
}

const RIGHT_CELL_CLASS = "ag-right-aligned-cell";
const RIGHT_HEADER_CLASS = "ag-right-aligned-header";

function staticClass(
  value: ColDef["cellClass"] | ColDef["headerClass"] | undefined,
): string | string[] | undefined {
  if (typeof value === "string" || Array.isArray(value)) return value;
  return undefined;
}

function appendClass(
  existing: string | string[] | undefined,
  extra: string,
): string {
  const parts =
    existing == null
      ? []
      : Array.isArray(existing)
        ? existing.flatMap((part) => part.split(/\s+/))
        : existing.split(/\s+/);
  const classes = parts.filter(Boolean);
  if (!classes.includes(extra)) classes.push(extra);
  return classes.join(" ");
}

function enrichDef(
  def: ColDef | ColGroupDef,
  defaultColDef?: ColDef,
): ColDef | ColGroupDef {
  if (isColumnGroup(def)) {
    return {
      ...def,
      children: (def.children ?? []).map((child) =>
        enrichDef(child, defaultColDef),
      ) as (ColDef | ColGroupDef)[],
    } as ColGroupDef;
  }

  const c = def as ColDef;
  if (
    typeof c.cellClass === "function" ||
    typeof c.headerClass === "function"
  ) {
    return c;
  }
  if (!isNumericLayoutColumn(c)) {
    return c;
  }

  const cellClass =
    staticClass(c.cellClass) ?? staticClass(defaultColDef?.cellClass);
  const headerClass =
    staticClass(c.headerClass) ?? staticClass(defaultColDef?.headerClass);

  return {
    ...c,
    cellClass: appendClass(cellClass, RIGHT_CELL_CLASS),
    headerClass: appendClass(headerClass, RIGHT_HEADER_CLASS),
  };
}

export function enrichAgGridColumnDefs(
  columnDefs: (ColDef | ColGroupDef)[],
  defaultColDef?: ColDef,
): (ColDef | ColGroupDef)[] {
  return columnDefs.map((d) => enrichDef(d, defaultColDef));
}
